import React, { useEffect, useState } from 'react';
import { Link as LinkIcon, Copy, Check, MessageCircle, RefreshCw, X } from 'lucide-react';
import { Button } from './ui/Button';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface Props {
  open: boolean;
  onClose: () => void;
  inviterName: string;
  generateInviteLink: () => Promise<string | null>;
}

export function InviteFriendModal({ open, onClose, inviterName, generateInviteLink }: Props) {
  const [link, setLink] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) {
      setLink(null);
      setCopied(false);
      setGenerating(false);
    }
  }, [open]);

  if (!open) return null;

  const handleGenerate = async () => {
    setGenerating(true);
    const l = await generateInviteLink();
    setGenerating(false);
    if (l) setLink(l);
  };

  const handleCopy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    if (!link) return;
    const message = `${inviterName} te convidou pro BoraTrocar!\n\nO app mais completo pra organizar e trocar figurinhas da Copa 2026. Marca o que você tem, vê suas repetidas e combina trocas com os amigos.\n\nCrie sua conta:\n${link}`;
    if (typeof navigator.share === 'function') {
      navigator.share({ text: message }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
    }
  };

  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Convidar amigo"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-lg p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] my-auto max-h-[90vh] overflow-y-auto animate-fade-up"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="absolute top-3 right-3 p-2 rounded-full text-ink-500 hover:text-ink-900 hover:bg-ink-100"
        >
          <X size={18} />
        </button>
        <h2 className="font-display font-semibold text-h2 text-ink-900 mb-1">Convidar amigo</h2>
        <p className="font-body text-small text-ink-500 mb-5">
          Gera um link reutilizável — quem clicar vira seu amigo no app.
        </p>

        {!link ? (
          <Button
            variant="primary"
            size="lg"
            fullWidth
            icon={LinkIcon}
            loading={generating}
            onClick={handleGenerate}
          >
            {generating ? 'Gerando link...' : 'Gerar link de convite'}
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 bg-ink-100 rounded-lg px-3 py-2.5 border border-ink-300">
              <span className="text-small text-ink-700 truncate flex-1 font-mono">{link}</span>
              <button
                type="button"
                onClick={handleCopy}
                aria-label={copied ? 'Link copiado' : 'Copiar link'}
                className="shrink-0 text-ink-500 hover:text-ink-900 transition-colors p-1.5 rounded-md btn-focus"
              >
                {copied ? <Check size={18} className="text-green-700" /> : <Copy size={18} />}
              </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="primary" size="md" fullWidth icon={MessageCircle} onClick={handleShare}>
                {canShare ? 'Compartilhar' : 'Enviar no WhatsApp'}
              </Button>
              <Button
                variant="secondary"
                size="md"
                fullWidth
                icon={RefreshCw}
                onClick={() => {
                  setLink(null);
                  setCopied(false);
                }}
              >
                Gerar novo
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
